import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { getIn, useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import type {
  KappaContact,
  KappaDeduction,
  KappaDocument,
  KappaEmail,
  KappaParent,
  KappaResidence,
  Partner
} from "@kappa-v2/contracts";
import { useApi } from "../../../hooks/useApi.js";
import { PARTNER_TYPE_OPTIONS } from "./PartnerFilters.js";
import { formLabel } from "../../../lib/labels.js";
import { useTranslation } from "react-i18next";
import MuiPhoneNumber from "material-ui-phone-number-2";
import { DialogHelp, type DialogHelpStep } from "../../../components/DialogHelp.js";

// Solo i valori (chiavi i18n form.*): l'etichetta si ottiene con formLabel(value).
const RELATIONSHIP_OPTIONS = [
  "form.relationship.father",
  "form.relationship.mother",
  "form.relationship.parent1",
  "form.relationship.parent2",
  "form.relationship.legal_guardian",
  "form.relationship.other"
];

const STREET_TYPE_OPTIONS = [
  "form.streettype.street",
  "form.streettype.square",
  "form.streettype.road",
  "form.streettype.esplanade",
  "form.streettype.avenue",
  "form.streettype.boulevard",
  "form.streettype.plaza",
  "form.streettype.alley"
];

const DOCUMENT_TYPE_OPTIONS = [
  "form.documentType.identity_card",
  "form.documentType.electronic_identity_card",
  "form.documentType.passport",
  "form.documentType.driving_license",
  "form.documentType.residence_permit",
  "form.documentType.health_card_with_photo"
];

const NUMBER_TYPE_OPTIONS = [
  "form.numberType.cell",
  "form.numberType.home",
  "form.numberType.office"
];

const EMAIL_TYPE_OPTIONS = [
  "form.emailType.personal",
  "form.emailType.office"
];

interface CountryOption {
  iso3361_3?: string;
  it_name?: string;
  en_name?: string;
}

interface MunicipalityApiItem {
  codice: string;
  nome: string;
  cap?: string;
  provincia?: {
    nome?: string;
    regione?: string;
  };
}

interface MunicipalityOption {
  codice: string;
  label: string;
  cap?: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  partnerType: string;
  fiscalCode: string;
  dateBirth: string;
  countryBirth: string;
  istatCodeBirth: string;
  active: boolean;
  contacts: KappaContact[];
  emails: KappaEmail[];
  document: KappaDocument;
  residence: KappaResidence;
  parent: KappaParent;
  deduction: KappaDeduction;
}

function emptyContact(): KappaContact {
  return {
    telephoneNumber: "",
    numberType: "form.numberType.cell",
    numberDescription: "",
    isMain: false
  };
}

function emptyEmail(): KappaEmail {
  return {
    email: "",
    emailType: "form.emailType.personal",
    emailDescription: "",
    isMain: false
  };
}

function emptyDocument(): KappaDocument {
  return {
    document_type: "form.documentType.identity_card",
    document_id: "",
    released_by: "",
    expires: ""
  };
}

function emptyResidence(): KappaResidence {
  return {
    streetType: "form.streettype.street",
    name: "",
    number: "",
    cap: "",
    istatCode: ""
  };
}

function emptyParent(): KappaParent {
  return {
    firstName: "",
    lastName: "",
    fiscalCode: "",
    relationship: "form.relationship.other"
  };
}

function emptyDeduction(): KappaDeduction {
  return {
    firstName: "",
    lastName: "",
    fiscalCode: ""
  };
}

function trimValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeLookupCode(value: unknown): string {
  return trimValue(value).toUpperCase();
}

function toDateInput(value?: string | Date | null): string {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

function toFormValues(partner: Partner | null): FormValues {
  return {
    firstName: partner?.firstName ?? "",
    lastName: partner?.lastName ?? "",
    partnerType: partner?.partnerType ?? "form.partnerType.horse",
    fiscalCode: partner?.fiscalCode ?? "",
    dateBirth: toDateInput(partner?.dateBirth),
    countryBirth: partner?.countryBirth ?? "ITA",
    istatCodeBirth: partner?.istatCodeBirth ?? "",
    active: partner?.active ?? true,
    contacts:
      partner?.contacts && partner.contacts.length > 0
        ? partner.contacts.map((contact) => ({ ...emptyContact(), ...contact }))
        : [emptyContact()],
    emails:
      partner?.emails && partner.emails.length > 0
        ? partner.emails.map((email) => ({ ...emptyEmail(), ...email }))
        : [emptyEmail()],
    document: { ...emptyDocument(), ...(partner?.document ?? {}) },
    residence: { ...emptyResidence(), ...(partner?.residence ?? {}) },
    parent: { ...emptyParent(), ...(partner?.parent ?? {}) },
    deduction: { ...emptyDeduction(), ...(partner?.deduction ?? {}) }
  };
}

function hasDocument(document: KappaDocument) {
  return Boolean(
    trimValue(document.document_id) || trimValue(document.released_by) || trimValue(document.expires)
  );
}

function hasResidence(residence: KappaResidence) {
  return Boolean(
    trimValue(residence.istatCode) ||
      trimValue(residence.cap) ||
      trimValue(residence.name) ||
      trimValue(residence.number)
  );
}

function hasParent(parent: KappaParent) {
  return Boolean(
    trimValue(parent.firstName) || trimValue(parent.lastName) || trimValue(parent.fiscalCode)
  );
}

function hasDeduction(deduction: KappaDeduction) {
  return Boolean(
    trimValue(deduction.firstName) ||
      trimValue(deduction.lastName) ||
      trimValue(deduction.fiscalCode)
  );
}

function normalizeContacts(contacts: KappaContact[]): KappaContact[] {
  return contacts
    .map((contact) => ({
      telephoneNumber: trimValue(contact.telephoneNumber),
      numberType: trimValue(contact.numberType) || "form.numberType.cell",
      numberDescription: trimValue(contact.numberDescription),
      isMain: false
    }))
    .filter((contact) => contact.telephoneNumber)
    .map((contact, index) => ({
      ...contact,
      numberDescription: contact.numberDescription || undefined,
      isMain: index === 0
    }));
}

function normalizeEmails(emails: KappaEmail[]): KappaEmail[] {
  return emails
    .map((email) => ({
      email: trimValue(email.email),
      emailType: trimValue(email.emailType) || "form.emailType.personal",
      emailDescription: trimValue(email.emailDescription),
      isMain: false
    }))
    .filter((email) => email.email)
    .map((email, index) => ({
      ...email,
      emailDescription: email.emailDescription || undefined,
      isMain: index === 0
    }));
}

function toPartnerBody(values: FormValues, isAdult: boolean): Partial<Partner> {
  const document = hasDocument(values.document)
    ? {
        document_type: trimValue(values.document.document_type) || "form.documentType.identity_card",
        document_id: trimValue(values.document.document_id) || undefined,
        released_by: trimValue(values.document.released_by) || undefined,
        expires: trimValue(values.document.expires) || undefined
      }
    : null;

  const residence = hasResidence(values.residence)
    ? {
        streetType: trimValue(values.residence.streetType) || "form.streettype.street",
        name: trimValue(values.residence.name) || undefined,
        number: trimValue(values.residence.number) || undefined,
        cap: trimValue(values.residence.cap) || undefined,
        istatCode: trimValue(values.residence.istatCode) || undefined
      }
    : null;
  const parent =
    (!isAdult && hasParent(values.parent)
      ? {
          firstName: trimValue(values.parent.firstName) || undefined,
          lastName: trimValue(values.parent.lastName) || undefined,
          fiscalCode: trimValue(values.parent.fiscalCode) || undefined,
          relationship: trimValue(values.parent.relationship) || undefined
        }
      : null) as unknown as Partner["parent"];

  const deduction =
    (!isAdult && hasDeduction(values.deduction)
      ? {
          firstName: trimValue(values.deduction.firstName) || undefined,
          lastName: trimValue(values.deduction.lastName) || undefined,
          fiscalCode: trimValue(values.deduction.fiscalCode) || undefined
        }
      : null) as unknown as Partner["deduction"];

  return {
    firstName: trimValue(values.firstName),
    lastName: trimValue(values.lastName),
    partnerType: trimValue(values.partnerType),
    fiscalCode: trimValue(values.fiscalCode) || undefined,
    dateBirth: trimValue(values.dateBirth) || undefined,
    countryBirth: trimValue(values.countryBirth) || undefined,
    istatCodeBirth: trimValue(values.istatCodeBirth) || undefined,
    active: values.active,
    contacts: normalizeContacts(values.contacts),
    emails: normalizeEmails(values.emails),
    document: document as Partner["document"],
    residence: residence as Partner["residence"],
    parent,
    deduction
  };
}

interface Props {
  open: boolean;
  partner: Partner | null;
  saving: boolean;
  onSave: (body: Partial<Partner>, id?: string) => Promise<void>;
  onClose: () => void;
}

export function PartnerFormDialog({ open, partner, saving, onSave, onClose }: Props) {
  const { t } = useTranslation();
  const { call } = useApi();
  const schema = Yup.object({
    firstName: Yup.string().trim().min(2, t("partnerForm.min2")).required(t("partnerForm.required")),
    lastName: Yup.string().trim().min(2, t("partnerForm.min2")).required(t("partnerForm.required")),
    partnerType: Yup.string().required(t("partnerForm.required")),
    emails: Yup.array().of(
      Yup.object({
        email: Yup.string().test("email-format", t("partnerForm.invalidEmail"), (value) => {
          if (!value) return true;
          return Yup.string().email().isValidSync(value);
        })
      })
    )
  });
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [municipalities, setMunicipalities] = useState<MunicipalityOption[]>([]);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const formik = useFormik<FormValues>({
    initialValues: toFormValues(partner),
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const isAdult =
        !values.dateBirth || dayjs().diff(dayjs(values.dateBirth), "year") >= 18;
      await onSave(toPartnerBody(values, isAdult), partner?._id);
      onClose();
    }
  });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const loadLookups = async () => {
      try {
        const [countryData, municipalityData] = await Promise.all([
          call<CountryOption[]>("/api/v1/address/countries"),
          call<MunicipalityApiItem[]>("/api/v1/address/municipalities")
        ]);

        if (cancelled) return;

        setCountries(countryData);
        setMunicipalities(
          municipalityData
            .map((item) => ({
              codice: item.codice,
              cap: item.cap,
              label: `${item.nome} (${item.provincia?.nome ?? "-"} - ${item.provincia?.regione ?? "-"})`
            }))
            .sort((left, right) => left.label.localeCompare(right.label, "it"))
        );
        setLookupError(null);
      } catch {
        if (cancelled) return;
        setLookupError(t("partnerForm.lookupError"));
      }
    };

    void loadLookups();

    return () => {
      cancelled = true;
    };
  }, [open, call]);

  const isAdult = useMemo(() => {
    if (!formik.values.dateBirth) return true;
    const birthDate = dayjs(formik.values.dateBirth);
    return birthDate.isValid() ? dayjs().diff(birthDate, "year") >= 18 : true;
  }, [formik.values.dateBirth]);

  const birthCountry = useMemo(
    () =>
      countries.find(
        (country) =>
          normalizeLookupCode(country.iso3361_3) === normalizeLookupCode(formik.values.countryBirth)
      ) ?? null,
    [countries, formik.values.countryBirth]
  );

  const birthMunicipality = useMemo(
    () =>
      municipalities.find(
        (item) =>
          normalizeLookupCode(item.codice) === normalizeLookupCode(formik.values.istatCodeBirth)
      ) ?? null,
    [municipalities, formik.values.istatCodeBirth]
  );

  const residenceMunicipality = useMemo(
    () =>
      municipalities.find(
        (item) =>
          normalizeLookupCode(item.codice) ===
          normalizeLookupCode(formik.values.residence.istatCode)
      ) ?? null,
    [municipalities, formik.values.residence.istatCode]
  );

  useEffect(() => {
    if (!open || !residenceMunicipality?.cap) return;
    if (trimValue(formik.values.residence.cap)) return;
    void formik.setFieldValue("residence.cap", residenceMunicipality.cap, false);
  }, [open, residenceMunicipality, formik.values.residence.cap, formik.setFieldValue]);

  const fieldProps = (name: string) => {
    const touched = getIn(formik.touched, name);
    const error = getIn(formik.errors, name);
    return {
      name,
      value: (getIn(formik.values, name) ?? "") as string,
      onChange: formik.handleChange,
      onBlur: formik.handleBlur,
      error: Boolean(touched && error),
      helperText: touched && typeof error === "string" ? error : undefined
    };
  };

  const addContact = () => {
    formik.setFieldValue("contacts", [...formik.values.contacts, emptyContact()]);
  };

  const removeContact = (index: number) => {
    formik.setFieldValue(
      "contacts",
      formik.values.contacts.filter((_, currentIndex) => currentIndex !== index)
    );
  };

  const addEmail = () => {
    formik.setFieldValue("emails", [...formik.values.emails, emptyEmail()]);
  };

  const removeEmail = (index: number) => {
    formik.setFieldValue(
      "emails",
      formik.values.emails.filter((_, currentIndex) => currentIndex !== index)
    );
  };

  const copyDeductionFromParent = () => {
    formik.setFieldValue("deduction.firstName", formik.values.parent.firstName);
    formik.setFieldValue("deduction.lastName", formik.values.parent.lastName);
    formik.setFieldValue("deduction.fiscalCode", formik.values.parent.fiscalCode);
  };

  const mode = isAdult ? t("partnerForm.adult") : t("partnerForm.minor");
  const title = partner
    ? t("partnerForm.titleEdit", { mode })
    : t("partnerForm.titleNew", { mode });

  const helpSteps: DialogHelpStep[] = [
    { element: '[data-tour="pf-active"]', title: t("help.partnerForm.active.title"), description: t("help.partnerForm.active.desc") },
    { element: '[data-tour="pf-type"]', title: t("help.partnerForm.type.title"), description: t("help.partnerForm.type.desc") },
    { element: '[data-tour="pf-name"]', title: t("help.partnerForm.name.title"), description: t("help.partnerForm.name.desc") },
    { element: '[data-tour="pf-residence"]', title: t("help.partnerForm.residence.title"), description: t("help.partnerForm.residence.desc") },
    { element: '[data-tour="pf-document"]', title: t("help.partnerForm.document.title"), description: t("help.partnerForm.document.desc") },
    { element: '[data-tour="pf-email"]', title: t("help.partnerForm.email.title"), description: t("help.partnerForm.email.desc") },
    { element: '[data-tour="pf-contacts"]', title: t("help.partnerForm.contacts.title"), description: t("help.partnerForm.contacts.desc") },
    { element: '[data-tour="pf-save"]', title: t("help.partnerForm.save.title"), description: t("help.partnerForm.save.desc") }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          {title}
          <DialogHelp steps={helpSteps} />
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {lookupError && <Alert severity="warning" sx={{ mb: 2 }}>{lookupError}</Alert>}

          <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Associato
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-tour="pf-active">
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.active}
                    onChange={(event) => formik.setFieldValue("active", event.target.checked)}
                    color="success"
                  />
                }
                label={formik.values.active ? t("common.active") : t("common.activeF")}
              />
            </Grid>
            <Grid item xs={12} sm={6} data-tour="pf-type">
              <FormControl fullWidth size="small">
                <InputLabel>{t("partnerForm.partnerType")}</InputLabel>
                <Select
                  label={t("partnerForm.partnerType")}
                  value={formik.values.partnerType}
                  onChange={(event) => formik.setFieldValue("partnerType", event.target.value)}
                >
                  {PARTNER_TYPE_OPTIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {formLabel(value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} data-tour="pf-name">
              <TextField fullWidth size="small" label={t("partnerForm.firstName")} {...fieldProps("firstName")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={t("partnerForm.lastName")} {...fieldProps("lastName")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              {countries.length > 0 ? (
                <Autocomplete
                  options={countries}
                  value={birthCountry}
                  getOptionLabel={(option) => option.it_name || option.en_name || option.iso3361_3 || ""}
                  isOptionEqualToValue={(option, value) =>
                    normalizeLookupCode(option.iso3361_3) === normalizeLookupCode(value.iso3361_3)
                  }
                  onChange={(_, value) =>
                    formik.setFieldValue("countryBirth", normalizeLookupCode(value?.iso3361_3))
                  }
                  onBlur={() => formik.setFieldTouched("countryBirth", true)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={t("partnerForm.birthCountry")}
                      helperText={fieldProps("countryBirth").helperText}
                      error={fieldProps("countryBirth").error}
                    />
                  )}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label={t("partnerForm.birthCountryIso")}
                  placeholder="ITA"
                  {...fieldProps("countryBirth")}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {municipalities.length > 0 ? (
                <Autocomplete
                  options={municipalities}
                  value={birthMunicipality}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    normalizeLookupCode(option.codice) === normalizeLookupCode(value.codice)
                  }
                  onChange={(_, value) =>
                    formik.setFieldValue("istatCodeBirth", normalizeLookupCode(value?.codice))
                  }
                  filterOptions={(options, state) =>
                    options.filter((option) =>
                      option.label.toLowerCase().includes(state.inputValue.toLowerCase())
                    )
                  }
                  onBlur={() => formik.setFieldTouched("istatCodeBirth", true)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={t("partnerForm.birthCity")}
                      helperText={fieldProps("istatCodeBirth").helperText}
                      error={fieldProps("istatCodeBirth").error}
                    />
                  )}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label={t("partnerForm.birthCityIstat")}
                  {...fieldProps("istatCodeBirth")}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t("partnerForm.birthDate")}
                InputLabelProps={{ shrink: true }}
                {...fieldProps("dateBirth")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={t("partnerForm.fiscalCode")} {...fieldProps("fiscalCode")} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Typography data-tour="pf-residence" variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {t("partnerForm.sectionResidence")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {municipalities.length > 0 ? (
                <Autocomplete
                  options={municipalities}
                  value={residenceMunicipality}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    normalizeLookupCode(option.codice) === normalizeLookupCode(value.codice)
                  }
                  onChange={(_, value) => {
                    void formik.setFieldValue(
                      "residence.istatCode",
                      normalizeLookupCode(value?.codice)
                    );
                    void formik.setFieldValue("residence.cap", trimValue(value?.cap));
                  }}
                  filterOptions={(options, state) =>
                    options.filter((option) =>
                      option.label.toLowerCase().includes(state.inputValue.toLowerCase())
                    )
                  }
                  onBlur={() => formik.setFieldTouched("residence.istatCode", true)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={t("partnerForm.residenceCity")}
                      helperText={fieldProps("residence.istatCode").helperText}
                      error={fieldProps("residence.istatCode").error}
                    />
                  )}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label={t("partnerForm.residenceCityIstat")}
                  {...fieldProps("residence.istatCode")}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={t("partnerForm.cap")} {...fieldProps("residence.cap")} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("partnerForm.streetType")}</InputLabel>
                <Select
                  label={t("partnerForm.streetType")}
                  value={formik.values.residence.streetType}
                  onChange={(event) => formik.setFieldValue("residence.streetType", event.target.value)}
                >
                  {STREET_TYPE_OPTIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {formLabel(value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={t("partnerForm.residenceName")} {...fieldProps("residence.name")} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label={t("partnerForm.civic")} {...fieldProps("residence.number")} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Typography data-tour="pf-document" variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {t("partnerForm.sectionDocument")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("partnerForm.documentType")}</InputLabel>
                <Select
                  label={t("partnerForm.documentType")}
                  value={formik.values.document.document_type}
                  onChange={(event) => formik.setFieldValue("document.document_type", event.target.value)}
                >
                  {DOCUMENT_TYPE_OPTIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {formLabel(value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t("partnerForm.documentId")}
                {...fieldProps("document.document_id")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t("partnerForm.expiryDate")}
                InputLabelProps={{ shrink: true }}
                {...fieldProps("document.expires")}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth size="small" label={t("partnerForm.releasedBy")} {...fieldProps("document.released_by")} />
            </Grid>
          </Grid>

          {!isAdult && (
            <>
              <Divider sx={{ my: 2.5 }} />

              <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {t("partnerForm.sectionLegalRep")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label={t("partnerForm.firstName")} {...fieldProps("parent.firstName")} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label={t("partnerForm.lastName")} {...fieldProps("parent.lastName")} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("partnerForm.relationship")}</InputLabel>
                    <Select
                      label={t("partnerForm.relationship")}
                      value={formik.values.parent.relationship}
                      onChange={(event) => formik.setFieldValue("parent.relationship", event.target.value)}
                    >
                      {RELATIONSHIP_OPTIONS.map((value) => (
                        <MenuItem key={value} value={value}>
                          {formLabel(value)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label={t("partnerForm.fiscalCode")} {...fieldProps("parent.fiscalCode")} />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2.5 }} />

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  {t("partnerForm.sectionDeduction")}
                </Typography>
                <Button size="small" onClick={copyDeductionFromParent}>
                  Copia da genitore
                </Button>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label={t("partnerForm.firstName")} {...fieldProps("deduction.firstName")} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label={t("partnerForm.lastName")} {...fieldProps("deduction.lastName")} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label={t("partnerForm.fiscalCode")} {...fieldProps("deduction.fiscalCode")} />
                </Grid>
              </Grid>
            </>
          )}

          <Divider sx={{ my: 2.5 }} />

          <Stack data-tour="pf-email" direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary">
              {t("partnerForm.sectionEmail")}
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addEmail}>
              {t("partnerForm.addEmail")}
            </Button>
          </Stack>
          <Grid container spacing={2}>
            {formik.values.emails.map((_, index) => (
              <React.Fragment key={`email-${index}`}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("partnerForm.emailLabel")}
                    {...fieldProps(`emails[${index}].email`)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("partnerForm.emailDescription")}
                    {...fieldProps(`emails[${index}].emailDescription`)}
                  />
                </Grid>
                <Grid item xs={10} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("partnerForm.emailType")}</InputLabel>
                    <Select
                      label={t("partnerForm.emailType")}
                      value={formik.values.emails[index]?.emailType ?? "form.emailType.personal"}
                      onChange={(event) =>
                        formik.setFieldValue(`emails[${index}].emailType`, event.target.value)
                      }
                    >
                      {EMAIL_TYPE_OPTIONS.map((value) => (
                        <MenuItem key={value} value={value}>
                          {formLabel(value)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2} sm={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IconButton color="error" onClick={() => removeEmail(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <Stack data-tour="pf-contacts" direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary">
              {t("partnerForm.sectionContacts")}
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addContact}>
              {t("partnerForm.addContact")}
            </Button>
          </Stack>
          <Grid container spacing={2}>
            {formik.values.contacts.map((_, index) => (
              <React.Fragment key={`contact-${index}`}>
                <Grid item xs={12} sm={4}>
                  <MuiPhoneNumber
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={t("partnerForm.phoneNumber")}
                    name={`contacts[${index}].telephoneNumber`}
                    value={formik.values.contacts[index]?.telephoneNumber ?? ""}
                    defaultCountry="it"
                    preferredCountries={["it"]}
                    countryCodeEditable={false}
                    onChange={(val) =>
                      formik.setFieldValue(
                        `contacts[${index}].telephoneNumber`,
                        typeof val === "string" ? val : val.target.value
                      )
                    }
                    onBlur={formik.handleBlur}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("partnerForm.phoneDescription")}
                    {...fieldProps(`contacts[${index}].numberDescription`)}
                  />
                </Grid>
                <Grid item xs={10} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("partnerForm.phoneType")}</InputLabel>
                    <Select
                      label={t("partnerForm.phoneType")}
                      value={formik.values.contacts[index]?.numberType ?? "form.numberType.cell"}
                      onChange={(event) =>
                        formik.setFieldValue(`contacts[${index}].numberType`, event.target.value)
                      }
                    >
                      {NUMBER_TYPE_OPTIONS.map((value) => (
                        <MenuItem key={value} value={value}>
                          {formLabel(value)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2} sm={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IconButton color="error" onClick={() => removeContact(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button data-tour="pf-save" type="submit" variant="contained" disabled={saving}>
            {saving ? t("common.saving") : partner ? t("partnerForm.saveChanges") : t("common.save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
